/* ==========================================================================
   BRASSLUXE MOROCCO — cart drawer & global cart interactions (ES module)
   Loaded once by sections/cart-drawer.liquid (statically rendered in layout).

   Owns, globally:
     [data-quick-add="variantId"]        click → add variant, open drawer
     form[action*="/cart/add"]           submit → AJAX add (with [data-ajax-add]
       [data-ajax-add|data-product-form]   or [data-product-form])
     [data-cart-open]                    click → open drawer
     [data-cart-drawer-close]            click → close drawer
     [data-cart-qty="minus|plus"]        click → change line qty (drawer only)
       + [data-line-key]
     [data-cart-remove] + [data-line-key] click → remove line (drawer only)

   After every mutation:
     - swaps drawer markup via the Section Rendering API (sections: cart-drawer)
     - updates every [data-cart-count] bubble from [data-cart-count-source]
     - dispatches document event 'blx:cart:updated' (detail: { cart })
   ========================================================================== */

const BLX = window.BrassLuxe || {};
const BLX_CFG = window.__BLX || {};
const ROUTES = BLX_CFG.routes || {};
const SECTION_ID = 'cart-drawer';

const drawerRoot = document.querySelector('[data-cart-drawer]');
if (!drawerRoot) {
  // No drawer on the page: leave every cart interaction to its native fallback.
} else {
  init();
}

function init() {
  let isOpen = false;
  let lastFocus = null;

  const qs = (sel, root) => (root || document).querySelector(sel);
  const getDrawer = () => qs('[data-cart-drawer]');
  const getPanel = () => qs('[data-cart-drawer-panel]');

  /* ---------- money formatter (cents → shop.money_format) ---------- */
  /* ---------- scroll lock (page + Lenis) ---------- */
  function lockScroll(lock) {
    document.body.style.overflow = lock ? 'hidden' : '';
    if (BLX.lenis) {
      if (lock) BLX.lenis.stop();
      else BLX.lenis.start();
    }
  }

  /* ---------- open / close ---------- */
  function open() {
    const drawer = getDrawer();
    if (!drawer || isOpen) return;
    isOpen = true;
    lastFocus = document.activeElement;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    lockScroll(true);

    const panel = getPanel();
    const backdrop = qs('[data-cart-drawer-backdrop]', drawer);
    if (BLX.gsap && !BLX.reducedMotion && panel) {
      BLX.gsap.fromTo(panel, { xPercent: 102 }, { xPercent: 0, duration: 0.6, ease: 'power3.out' });
      if (backdrop) BLX.gsap.fromTo(backdrop, { opacity: 0 }, { opacity: 1, duration: 0.4 });
    }
    if (panel) panel.focus({ preventScroll: true });
    document.addEventListener('keydown', onKeydown);
  }

  function close() {
    const drawer = getDrawer();
    if (!drawer || !isOpen) return;
    isOpen = false;
    document.removeEventListener('keydown', onKeydown);

    const finish = () => {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      lockScroll(false);
      if (lastFocus && typeof lastFocus.focus === 'function') {
        lastFocus.focus({ preventScroll: true });
      }
    };

    const panel = getPanel();
    const backdrop = qs('[data-cart-drawer-backdrop]', drawer);
    if (BLX.gsap && !BLX.reducedMotion && panel) {
      BLX.gsap.to(panel, { xPercent: 102, duration: 0.45, ease: 'power3.in', onComplete: finish });
      if (backdrop) BLX.gsap.to(backdrop, { opacity: 0, duration: 0.4 });
    } else {
      finish();
    }
  }

  /* ---------- focus trap + Esc ---------- */
  function onKeydown(event) {
    if (event.key === 'Escape') { close(); return; }
    if (event.key !== 'Tab') return;
    const panel = getPanel();
    if (!panel) return;
    const focusables = panel.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (!focusables.length) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /* ---------- error surface (never a silent fail) ---------- */
  function showError(message) {
    const drawer = getDrawer();
    if (!drawer) return;
    const err = qs('[data-cart-error]', drawer);
    if (err) {
      err.textContent = message || 'Something went wrong. Please try again.';
      err.hidden = false;
    }
    if (!isOpen) open();
  }

  function clearError() {
    const err = qs('[data-cart-error]');
    if (err) { err.hidden = true; err.textContent = ''; }
  }

  function setBusy(busy) {
    const drawer = getDrawer();
    if (drawer) drawer.classList.toggle('is-busy', busy);
    const panel = getPanel();
    if (panel) panel.setAttribute('aria-busy', busy ? 'true' : 'false');
  }

  /* ---------- fetch helpers ---------- */
  async function postJSON(url, body) {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    });
    const data = await response.json().catch(() => null);
    if (!response.ok) {
      const message = data && (data.description || data.message);
      throw new Error(message || 'The cart could not be updated. Please try again.');
    }
    return data;
  }

  async function postForm(url, formData) {
    const response = await fetch(url, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: formData
    });
    const data = await response.json().catch(() => null);
    if (!response.ok) {
      const message = data && (data.description || data.message);
      throw new Error(message || 'The item could not be added. Please try again.');
    }
    return data;
  }

  async function fetchCart() {
    try {
      const response = await fetch(ROUTES.cartGet, { headers: { Accept: 'application/json' } });
      if (!response.ok) return null;
      return await response.json();
    } catch (e) {
      return null;
    }
  }

  /* ---------- DOM updates ---------- */
  function updateCountBubbles(count) {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.hidden = count === 0;
    });
  }

  function animateShippingBar(fromWidth) {
    const bar = qs('[data-shipping-bar]');
    if (!bar) return;
    const fill = qs('[data-shipping-bar-fill]', bar);
    const threshold = parseInt(bar.getAttribute('data-threshold'), 10) || 0;
    const total = parseInt(bar.getAttribute('data-total'), 10) || 0;
    if (threshold <= 0) return;

    const remaining = threshold - total;
    const unlocked = remaining <= 0;
    bar.classList.toggle('is-unlocked', unlocked);

    if (fill) {
      const pct = Math.min(100, Math.round((total / threshold) * 100));
      if (fromWidth != null) {
        fill.style.transition = 'none';
        fill.style.width = fromWidth;
        void fill.offsetWidth; // reflow so the transition restarts from the old width
        fill.style.transition = '';
      }
      fill.style.width = pct + '%';
    }
  }

  function swapDrawer(sections) {
    const html = sections && sections[SECTION_ID];
    if (!html) return;
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const fresh = parsed.querySelector('[data-cart-drawer]');
    const current = getDrawer();
    if (!fresh || !current) return;

    const currentFill = qs('[data-shipping-bar-fill]', current);
    const previousWidth = currentFill ? currentFill.style.width : null;

    current.innerHTML = fresh.innerHTML;
    if (isOpen) current.classList.add('is-open');

    animateShippingBar(previousWidth);

    const countSource = qs('[data-cart-count-source]', current);
    if (countSource) updateCountBubbles(parseInt(countSource.textContent, 10) || 0);
  }

  function dispatchUpdated(cart) {
    if (!cart) return;
    document.dispatchEvent(new CustomEvent('blx:cart:updated', { detail: { cart } }));
  }

  async function afterMutation() {
    const cart = await fetchCart();
    if (cart) {
      updateCountBubbles(cart.item_count);
      dispatchUpdated(cart);
    }
  }

  /* ---------- mutations ---------- */
  async function quickAdd(button) {
    const variantId = button.getAttribute('data-quick-add');
    if (!variantId) return;
    button.disabled = true;
    clearError();
    try {
      const data = await postJSON(ROUTES.cartAdd + '.js', {
        items: [{ id: Number(variantId), quantity: 1 }],
        sections: SECTION_ID
      });
      swapDrawer(data && data.sections);
      open();
      await afterMutation();
    } catch (error) {
      showError(error.message);
    } finally {
      button.disabled = false;
    }
  }

  async function ajaxFormAdd(form) {
    const submit = form.querySelector('[type="submit"]');
    if (submit) submit.disabled = true;
    clearError();
    try {
      const formData = new FormData(form);
      formData.append('sections', SECTION_ID);
      const data = await postForm(ROUTES.cartAdd + '.js', formData);
      swapDrawer(data && data.sections);
      open();
      await afterMutation();
    } catch (error) {
      showError(error.message);
    } finally {
      if (submit) submit.disabled = false;
    }
  }

  async function changeLine(key, quantity) {
    if (!key) return;
    setBusy(true);
    clearError();
    try {
      const cart = await postJSON(ROUTES.cartChange + '.js', {
        id: key,
        quantity: quantity,
        sections: SECTION_ID
      });
      swapDrawer(cart && cart.sections);
      updateCountBubbles(cart.item_count);
      dispatchUpdated(cart);
    } catch (error) {
      showError(error.message);
    } finally {
      setBusy(false);
    }
  }

  function handleQtyButton(button) {
    const key = button.getAttribute('data-line-key');
    const wrapper = button.closest('[data-qty]');
    const input = wrapper ? qs('[data-cart-qty-input]', wrapper) : null;
    const current = input ? parseInt(input.value, 10) || 0 : 0;
    const next = button.getAttribute('data-cart-qty') === 'plus' ? current + 1 : Math.max(0, current - 1);
    if (next === current) return;
    changeLine(key, next);
  }

  /* ---------- global delegated listeners ---------- */
  document.addEventListener('click', (event) => {
    const quickAddBtn = event.target.closest('[data-quick-add]');
    if (quickAddBtn) {
      event.preventDefault();
      quickAdd(quickAddBtn);
      return;
    }

    const openBtn = event.target.closest('[data-cart-open]');
    if (openBtn) {
      event.preventDefault();
      open();
      return;
    }

    const closeBtn = event.target.closest('[data-cart-drawer-close]');
    if (closeBtn && closeBtn.closest('[data-cart-drawer]')) {
      event.preventDefault();
      close();
      return;
    }

    const qtyBtn = event.target.closest('[data-cart-qty]');
    if (qtyBtn && qtyBtn.closest('[data-cart-drawer]')) {
      event.preventDefault();
      handleQtyButton(qtyBtn);
      return;
    }

    const removeBtn = event.target.closest('[data-cart-remove]');
    if (removeBtn && removeBtn.closest('[data-cart-drawer]')) {
      event.preventDefault();
      changeLine(removeBtn.getAttribute('data-line-key'), 0);
    }
  });

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const action = form.getAttribute('action') || '';
    if (!action.includes('/cart/add')) return;
    if (!form.hasAttribute('data-ajax-add') && !form.hasAttribute('data-product-form')) return;
    event.preventDefault();
    ajaxFormAdd(form);
  });
}
